/**
 * Constantes del dominio del Cotizador de puertas.
 *
 * Permiso de gating + datos de presentación del formulario (modelos, calibres, sub-modelos
 * de dilatación con vidrio, estilos Verona, opciones de modo de cobro y calibre de marco).
 * Replican EXACTAMENTE los del SSR (`templates/cotizador_puertas.html`): mismos `value`,
 * mismas etiquetas y mismo estado inicial, para que la pantalla React produzca el mismo
 * payload y se vea igual. El cálculo lo hace el motor en el servidor (zona prohibida).
 */
import type { CotizadorInput } from "./types";

/**
 * Permiso que gobierna el Cotizador de puertas (igual que el menú/SSR/API:
 * `cotizador_controller.py` y `domains/cotizador.py` exigen `use_cotizador`; `navConfig.ts`
 * lo usa para la visibilidad del enlace).
 */
export const COTIZADOR_PERMISSION = "use_cotizador";

/** Un modelo de puerta seleccionable (`value` = `modelo_id` que envía el formulario). */
export interface ModeloOption {
  value: string;
  label: string;
}

/** Modelos de puerta (mismos `value`/etiqueta que el `<select id="modelo">` del SSR). */
export const MODELOS: ModeloOption[] = [
  { value: "1", label: "1. Estándar (V)" },
  { value: "2", label: "2. Combinada" },
  { value: "3", label: "3. Verona" },
  { value: "4", label: "4. Dilatación" },
  { value: "5", label: "5. Tablero troquelado" },
  { value: "6", label: "6. Tablero troquelado con hueco" },
  { value: "7", label: "7. Dilatación con vidrio" },
];

/** Etiqueta corta de un modelo por su id (para el historial; réplica de `modeloLabel` SSR). */
export const MODELO_LABELS: Record<string, string> = {
  "1": "V (Estándar)",
  "2": "Combinada",
  "3": "Verona",
  "4": "Dilatación",
  "5": "Tablero troquelado",
  "6": "Tablero troquelado con hueco",
  "7": "Dilatación con vidrio",
};

/** Calibres de hoja seleccionables (mismos que el `<select id="calibre">` del SSR). */
export const CALIBRES = ["16", "18", "20", "22"] as const;

/** Opción de calibre de marco (`igual` = mismo que la hoja). */
export interface CalibreMarcoOption {
  value: string;
  label: string;
}

/** Calibres de marco (réplica del radiogroup `calibre_marco` del SSR). */
export const CALIBRES_MARCO: CalibreMarcoOption[] = [
  { value: "igual", label: "Igual" },
  { value: "18", label: "CAL18" },
  { value: "20", label: "CAL20" },
  { value: "22", label: "CAL22" },
];

/** Modo de cobro (solo admin lo elige; los no-admin van fijos a `m2`). */
export interface ModoCobroOption {
  value: string;
  label: string;
}

/** Modos de cobro (réplica del radiogroup `modo_cobro` del SSR; admin). */
export const MODOS_COBRO: ModoCobroOption[] = [
  { value: "auto", label: "Auto" },
  { value: "pesos", label: "Pesos" },
  { value: "m2", label: "M²" },
];

/** Sub-modelo de Dilatación con vidrio (modelo 7); réplica de las tarjetas DV del SSR. */
export interface SubModeloDvOption {
  value: number;
  code: string;
  name: string;
  desc: string;
}

/** Sub-modelos de Dilatación con vidrio (réplica de `sub_modelo_dv` del SSR). */
export const SUB_MODELOS_DV: SubModeloDvOption[] = [
  { value: 1, code: "M1", name: "Lateral dcho", desc: "Lámina izq / vidrio dcho" },
  { value: 2, code: "M2", name: "Recuadro cen", desc: "Vidrio recuadro central" },
  { value: 3, code: "M3", name: "Centro-alto", desc: "Vidrio arriba / lámina abajo" },
  { value: 4, code: "M4", name: "Centro full", desc: "Lámina lados / vidrio centro" },
];

/** Un estilo Verona (modelo 3); réplica del `<select id="verona_estilo">` del SSR. */
export interface VeronaEstiloOption {
  value: string;
  label: string;
}

/** Estilos Verona (réplica de `verona_estilo` del SSR). */
export const VERONA_ESTILOS: VeronaEstiloOption[] = [
  { value: "||", label: "|| (Doble línea)" },
  { value: "=", label: "= (Igual)" },
];

/** Modelos que muestran el campo "número de venas" (Verona, Dilatación, Dilatación vidrio). */
export const MODELOS_CON_VENAS = new Set(["3", "4", "7"]);

/** Modelo de Dilatación con vidrio (muestra ancho de vidrio + sub-modelo). */
export const MODELO_DILATACION_VIDRIO = "7";

/** Modelo de Dilatación (muestra espacio por vena). */
export const MODELO_DILATACION = "4";

/** Modelo Verona (muestra el estilo). */
export const MODELO_VERONA = "3";

/**
 * Estado inicial del formulario (mismos valores por defecto que el SSR: modelo 1, calibre
 * 20, 1 puerta, medidas iguales, modo auto, marco igual). `ancho`/`alto` arrancan vacíos
 * (los teclea el usuario); el resto con los valores del SSR.
 */
export const INITIAL_FORM: CotizadorInput = {
  modelo_id: "1",
  calibre: "20",
  cantidad: 1,
  chapa_bola: false,
  venas: 4,
  ancho_vena: 3.0,
  sub_modelo_dv: 1,
  calibre_marco: "igual",
  verona_estilo: "||",
  medidas_diferentes: false,
  modo_cobro: "auto",
};

/** Mensaje genérico ante un error inesperado (no-API). */
export const COTIZADOR_GENERIC_ERROR = "Ocurrió un error inesperado.";
